import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { showSuccess, showError } from "@/utils/toast";

interface UploadTeleportModalProps {
    isOpen: boolean;
    onClose: () => void;
    uploadId: number;
    currentGameId: number;
    currentGameName: string;
}

interface GameOption {
    id: number;
    name: string;
    slug: string;
}

const UploadTeleportModal: React.FC<UploadTeleportModalProps> = ({
    isOpen,
    onClose,
    uploadId,
    currentGameId,
    currentGameName
}) => {
    const [gameSearchQuery, setGameSearchQuery] = useState('');
    const [gameSearchResults, setGameSearchResults] = useState<GameOption[]>([]);
    const [selectedTargetGameId, setSelectedTargetGameId] = useState<number | null>(null);
    const [targetGameName, setTargetGameName] = useState('');
    const [isSearchingGames, setIsSearchingGames] = useState(false);
    const [isTeleporting, setIsTeleporting] = useState(false);
    const [teleportResult, setTeleportResult] = useState<{
        success: boolean;
        message: string;
    } | null>(null);

    const handleGameSearch = async (query: string) => {
        setGameSearchQuery(query);
        if (query.length < 1) {
            setGameSearchResults([]);
            return;
        }

        setIsSearchingGames(true);
        try {
            const response = await fetch(`/api/games/search?q=${encodeURIComponent(query)}`);

            if (response.ok) {
                const data = await response.json();
                setGameSearchResults(data || []);
            } else {
                showError('Hiba a játék keresés során.');
                setGameSearchResults([]);
            }
        } catch (err) {
            showError('Hálózati hiba a játék keresés során.');
            setGameSearchResults([]);
        } finally {
            setIsSearchingGames(false);
        }
    };

    const handleTeleport = async () => {
        if (!selectedTargetGameId) {
            showError('Kérlek, válassz egy céljátékot!');
            return;
        }

        if (selectedTargetGameId === currentGameId) {
            showError('A céljáték nem lehet ugyanaz, mint a jelenlegi!');
            return;
        }

        if (!confirm(`Biztosan szeretnéd ezt a feltöltést (ID: ${uploadId}) a "${targetGameName}" játékra költöztetni?\n\nEz visszavonhatatlan!`)) {
            return;
        }

        setIsTeleporting(true);
        setTeleportResult(null);

        try {
            // CSRF token lekérése
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

            // Debug log
            console.log('📤 Teleport request:', {
                upload_id: uploadId,
                target_game_id: selectedTargetGameId
            });

            const response = await fetch(`/api/uploads/teleport`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify({
                    upload_id: uploadId,
                    target_game_id: selectedTargetGameId
                })
            });

            if (response.ok) {
                const result = await response.json();
                setTeleportResult({
                    success: true,
                    message: `✅ Sikeresen átköltözteted a feltöltést!\n${result.data?.downloads_moved || 0} letöltés követi az új játékot.`
                });
                showSuccess(`✅ Feltöltés átköltöztetése sikeres!`);

                // Bezárás 2 másodperc után
                setTimeout(() => {
                    onClose();
                    resetForm();
                }, 2000);
            } else {
                const errorData = await response.json();
                setTeleportResult({
                    success: false,
                    message: errorData.error || 'Hiba történt az átköltöztetés során.'
                });
                showError(errorData.error || 'Hiba történt az átköltöztetés során.');
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Ismeretlen hiba';
            setTeleportResult({
                success: false,
                message: `Hálózati hiba: ${errorMsg}`
            });
            showError(`Hálózati hiba: ${errorMsg}`);
        } finally {
            setIsTeleporting(false);
        }
    };

    const resetForm = () => {
        setGameSearchQuery('');
        setGameSearchResults([]);
        setSelectedTargetGameId(null);
        setTargetGameName('');
        setTeleportResult(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>🚀 Feltöltés Átköltöztetése</DialogTitle>
                    <DialogDescription>
                        Upload ID: {uploadId} | Jelenlegi játék: {currentGameName}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Game Search */}
                    <div>
                        <label className="text-sm font-semibold mb-2 block">Melyik játékra szeretnéd költöztetni?</label>
                        <div className="relative flex gap-2">
                            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                            <Input
                                type="text"
                                placeholder="Játék neve..."
                                value={gameSearchQuery}
                                onChange={(e) => handleGameSearch(e.target.value)}
                                className="pl-10 flex-1"
                            />
                            {isSearchingGames && <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />}
                        </div>
                    </div>

                    {/* Search Results */}
                    {gameSearchResults.length > 0 && (
                        <div className="border rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                            {gameSearchResults.map((game) => (
                                <button
                                    key={game.id}
                                    onClick={() => {
                                        setSelectedTargetGameId(game.id);
                                        setTargetGameName(game.name);
                                        setGameSearchQuery('');
                                        setGameSearchResults([]);
                                    }}
                                    className="w-full text-left p-2 rounded hover:bg-orange-500/20 border border-transparent hover:border-orange-500 transition"
                                >
                                    <p className="font-semibold text-sm">{game.name}</p>
                                    <p className="text-xs text-gray-500">ID: {game.id}</p>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Selected Game */}
                    {selectedTargetGameId && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 font-semibold">✓ Kiválasztott: {targetGameName}</p>
                        </div>
                    )}

                    {/* Teleport Result */}
                    {teleportResult && (
                        <div className={`p-3 rounded-lg border-2 flex gap-2 items-start ${teleportResult.success
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                            }`}>
                            {teleportResult.success ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <p className={teleportResult.success ? "text-green-800 text-sm" : "text-red-800 text-sm"}>
                                {teleportResult.message}
                            </p>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-2 pt-4">
                        <Button
                            onClick={handleTeleport}
                            disabled={!selectedTargetGameId || isTeleporting}
                            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            {isTeleporting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Átköltöztetés folyamatban...
                                </>
                            ) : (
                                "🚀 Átköltöztetés"
                            )}
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="outline"
                            className="flex-1"
                        >
                            Bezárás
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UploadTeleportModal;
